import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { billingApi, publicBillingApi } from '@/api/endpoints';
import type { PlanTier } from '@/types/enums';
import { useWorkspace } from './useAuth';
import { queryKeys } from './queryKeys';

/**
 * The pricing table.
 *
 * Not keyed by workspace — the catalog is the same four rows for everybody, and
 * a long stale time is safe because a price change is a deploy or a deliberate
 * correction, not something a customer's action can cause.
 */
export function usePlans() {
  return useQuery({
    queryKey: queryKeys.billing.plans,
    queryFn: () => billingApi.plans(),
    staleTime: 5 * 60_000,
  });
}

/**
 * The same table, for the marketing site.
 *
 * Separate from `usePlans` rather than a parameter on it, because the two hit
 * different endpoints and cache under different keys — this one must stay
 * usable with no token and no workspace, and sharing a key would let an
 * authenticated fetch populate the anonymous page with the secured shape.
 *
 * `retry: false`: pricing is decoration on a page whose job is to explain the
 * product. If it cannot load, the page still has to render.
 */
export function usePublicPlans() {
  return useQuery({
    queryKey: queryKeys.billing.publicPlans,
    queryFn: () => publicBillingApi.plans(),
    staleTime: 10 * 60_000,
    retry: false,
  });
}

/** How often to re-ask while a payment is being confirmed. */
const SETTLING_POLL_MS = 3_000;

/**
 * Plan and usage in one call.
 *
 * A short stale time rather than `Infinity`: a webhook can change the plan at
 * any instant, and the whole point of this screen is to reflect what just
 * happened after a checkout. This is also why the subscription does not ride on
 * `/auth/me`, which is cached with `staleTime: Infinity` and would never
 * refetch on its own.
 *
 * **`settling` is for the seconds right after a checkout**, and it exists
 * because a single refetch on arrival loses a race it cannot win. The gateway
 * hands the browser back immediately; the server validates the payment
 * server-to-server in a background job that runs on a ten-second tick. Asking
 * once, instantly, therefore reads the *old* plan almost every time — which
 * looks exactly like the payment having failed. Poll until the answer changes
 * instead, and pass `staleTime: 0` so each tick is a real request rather than
 * the cache answering itself.
 */
export function useBillingOverview(options: { settling?: boolean } = {}) {
  const { activeId } = useWorkspace();

  return useQuery({
    queryKey: queryKeys.billing.overview(activeId ?? ''),
    queryFn: () => billingApi.overview(),
    enabled: !!activeId,
    staleTime: options.settling ? 0 : 30_000,
    refetchInterval: options.settling ? SETTLING_POLL_MS : false,
  });
}

/**
 * Payment history. OWNER only — a member gets a 403 and an empty table.
 *
 * **`settling` polls it, because it is the evidence the billing screen waits
 * on.** A payment's own row is what says whether the money landed: SSLCommerz
 * writes it `PENDING` before the redirect and flips it to `PAID` or `FAILED`,
 * and Stripe writes a `PAID` row when the invoice settles. Watching the
 * subscription alone cannot answer the question — see `BillingPage`.
 */
export function useBillingTransactions(
  enabled = true,
  options: { settling?: boolean } = {}
) {
  const { activeId } = useWorkspace();

  return useQuery({
    queryKey: queryKeys.billing.transactions(activeId ?? ''),
    queryFn: () => billingApi.transactions(),
    enabled: !!activeId && enabled,
    staleTime: options.settling ? 0 : undefined,
    refetchInterval: options.settling ? SETTLING_POLL_MS : false,
  });
}

/**
 * Start a checkout and hand the browser to the gateway.
 *
 * The redirect happens here rather than in the component because every caller
 * does the same thing with the result, and a checkout URL that is fetched and
 * then not navigated to is a session the customer never sees and the gateway
 * eventually expires.
 */
export function useStartStripeCheckout() {
  return useMutation({
    mutationFn: (tier: PlanTier) => billingApi.startStripeCheckout(tier),
    onSuccess: ({ url }) => {
      window.location.href = url;
    },
  });
}

export function useStartSslcommerzCheckout() {
  return useMutation({
    mutationFn: ({ tier, months }: { tier: PlanTier; months: number }) =>
      billingApi.startSslcommerzCheckout(tier, months),
    onSuccess: ({ url }) => {
      window.location.href = url;
    },
  });
}

export function useOpenBillingPortal() {
  return useMutation({
    mutationFn: () => billingApi.openPortal(),
    onSuccess: ({ url }) => {
      window.location.href = url;
    },
  });
}

/**
 * Refetch everything a plan change affects.
 *
 * Called after returning from a checkout. The plan is the input to every quota
 * on every screen, so a stale cache after an upgrade shows the old limits and
 * the old refusals — which reads as the payment not having worked.
 */
export function useRefreshBilling() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.billing.all });
    void queryClient.invalidateQueries({ queryKey: queryKeys.usage.all });
  };
}
